import { Ball, SimulationConfig, EscapeRing, ShapeType } from '@/types';
import { IRenderer } from './IRenderer';
import { PerformanceTelemetry } from '@/services/PerformanceTelemetry';
import { WebGPUComputeManager, WebGPUBallComputeSubsystem } from './WebGPUCompute';
import { WebGPUParticleSystem } from './WebGPUParticleSystem';
import { FrameScheduler } from '@/services/FrameScheduler';
import { RenderGraph } from './RenderGraph';
import { PersistentBufferRing, GPUBindGroupCache, WebGPURenderBundleCache, getUniqueResourceId, WebGPUTextureStreamer, WebGPUVRAMGatekeeper } from './WebGPUMemory';

// Standard WebGPU Constants
const GPUBufferUsage = {
  MAP_READ: 0x0001,
  COPY_SRC: 0x0004,
  COPY_DST: 0x0008,
  INDEX: 0x0010,
  VERTEX: 0x0020,
  UNIFORM: 0x0040,
  STORAGE: 0x0080,
  QUERY_RESOLVE: 0x0200
};

const GPUShaderStage = {
  VERTEX: 0x1,
  FRAGMENT: 0x2,
  COMPUTE: 0x4
};

// WGSL Shaders
const BALL_WGSL = `
struct ShaderGlobal {
    u_resolution : vec2<f32>,
    u_translate  : vec2<f32>,
    u_scale      : f32,
    u_padding    : f32,
};

struct VertexInput {
    @location(0) position : vec2<f32>,
};

struct InstanceInput {
    @location(1) center : vec2<f32>,
    @location(2) color  : vec3<f32>,
    @location(3) radius : f32,
    @location(4) lodIndex : f32,
};

struct VertexOutput {
    @builtin(position) clipPos : vec4<f32>,
    @location(0) color        : vec4<f32>,
    @location(1) localPos     : vec2<f32>,
    @location(2) @interpolate(flat) lodIndex : f32,
};

@group(0) @binding(0) var<uniform> global : ShaderGlobal;

@vertex
fn vs_main(vertex : VertexInput, instance : InstanceInput) -> VertexOutput {
    var out : VertexOutput;
    let globalPos = (vertex.position * instance.radius * global.u_scale) + instance.center + global.u_translate;
    let normalized = (globalPos / global.u_resolution) * 2.0 - vec2<f32>(1.0, 1.0);
    out.clipPos = vec4<f32>(normalized.x, -normalized.y, 0.0, 1.0);
    out.color = vec4<f32>(instance.color, 1.0);
    out.localPos = vertex.position;
    out.lodIndex = instance.lodIndex;
    return out;
}

@fragment
fn fs_main(in : VertexOutput) -> @location(0) vec4<f32> {
    let dist = length(in.localPos);
    if (dist > 1.0) { discard; }
    
    // Task 9.2: For Low Detail (LOD 2) circles, bypass smoothstep to optimize performance on fine elements
    if (in.lodIndex > 1.5) {
        if (dist > 0.95) {
            return vec4<f32>(0.0, 0.0, 0.0, 0.55);
        }
        return in.color;
    }
    
    let borderFactor = smoothstep(0.93, 0.98, dist);
    return mix(in.color, vec4<f32>(0.0, 0.0, 0.0, 0.55), borderFactor);
}
`;

const RING_WGSL = `
struct RingGlobal {
    u_resolution: vec2<f32>,
    u_translate: vec2<f32>,
    u_scale: f32,
    u_thickness: f32,
    u_center: vec2<f32>,
    u_shape: f32,
    u_nextShape: f32,
    u_morphFactor: f32,
    u_rotation: f32,
};

struct VertexInput {
    @location(0) position : vec2<f32>,
};

struct InstanceInput {
    @location(1) radius  : f32,
    @location(2) angle   : f32,
    @location(3) color   : vec3<f32>,
    @location(4) alpha   : f32,
    @location(5) gapSize : f32,
};

struct VertexOutput {
    @builtin(position) clipPos : vec4<f32>,
    @location(0) pos           : vec2<f32>,
    @location(1) color         : vec4<f32>,
    @location(2) radius        : f32,
    @location(3) angle         : f32,
    @location(4) gapSize       : f32,
};

@group(0) @binding(0) var<uniform> global: RingGlobal;

@vertex
fn vs_main(vertex : VertexInput, instance : InstanceInput) -> VertexOutput {
    var out : VertexOutput;
    let padding = global.u_thickness + 5.0;
    let size = instance.radius + padding;
    let localPos = vertex.position * size;
    out.pos = localPos;
    
    let worldPos = localPos + global.u_center;
    let camPos = (worldPos * global.u_scale) + global.u_translate;
    let normalized = (camPos / global.u_resolution) * 2.0 - vec2<f32>(1.0, 1.0);
    out.clipPos = vec4<f32>(normalized.x, -normalized.y, 0.0, 1.0);
    out.color = vec4<f32>(instance.color, instance.alpha);
    out.radius = instance.radius;
    out.angle = instance.angle;
    out.gapSize = instance.gapSize;
    return out;
}

fn getRadiusAtAngle(angle: f32, n: i32) -> f32 {
    if (n == 0) { return 1.0; }
    let PI = 3.14159265359;
    let n_f = f32(n);
    let rem = angle % ((2.0 * PI) / n_f);
    let sector = rem - PI / n_f;
    return cos(PI / n_f) / cos(sector);
}

fn sdShape(p: vec2<f32>, shape: i32, r: f32, rotation: f32) -> f32 {
    if (shape == 0) { return length(p) - r; }
    let angle = atan2(p.y, p.x) - rotation;
    let shapeR = r * getRadiusAtAngle(angle, shape);
    return length(p) - shapeR;
}

@fragment
fn fs_main(in : VertexOutput) -> @location(0) vec4<f32> {
    let d1 = sdShape(in.pos, i32(global.u_shape), in.radius, global.u_rotation);
    let d2 = sdShape(in.pos, i32(global.u_nextShape), in.radius, global.u_rotation);
    let dist = mix(d1, d2, global.u_morphFactor);
    
    let halfThick = global.u_thickness * 0.5;
    let alpha = 1.0 - smoothstep(halfThick - 1.0, halfThick, abs(dist) * global.u_scale);
    if (alpha < 0.01) { discard; }
    
    let PI = 3.14159265359;
    let currentAngle = atan2(in.pos.y, in.pos.x);
    let diff = abs((currentAngle - in.angle + PI) % (2.0 * PI) - PI);
    if (diff < in.gapSize * 0.5) { discard; }
    
    return vec4<f32>(in.color.rgb, alpha * in.color.a);
}
`;

const BLAST_WGSL = `
struct BlastGlobal {
    u_resolution: vec2<f32>,
    u_translate: vec2<f32>,
    u_scale: f32,
    u_thickness: f32,
};

struct VertexInput {
    @location(0) position: vec2<f32>,
};

struct InstanceInput {
    @location(1) center: vec2<f32>,
    @location(2) radius: f32,
    @location(3) color: vec3<f32>,
    @location(4) alpha: f32,
};

struct VertexOutput {
    @builtin(position) clipPos: vec4<f32>,
    @location(0) uv: vec2<f32>,
    @location(1) color: vec4<f32>,
    @location(2) radius: f32,
};

@group(0) @binding(0) var<uniform> global: BlastGlobal;

@vertex
fn vs_main(vertex: VertexInput, instance: InstanceInput) -> VertexOutput {
    var out: VertexOutput;
    out.uv = vertex.position;
    out.color = vec4<f32>(instance.color, instance.alpha);
    out.radius = instance.radius;
    let worldPos = (vertex.position * instance.radius) + instance.center;
    let camPos = (worldPos * global.u_scale) + global.u_translate;
    let normalized = (camPos / global.u_resolution) * 2.0 - vec2<f32>(1.0, 1.0);
    out.clipPos = vec4<f32>(normalized.x, -normalized.y, 0.0, 1.0);
    return out;
}

@fragment
fn fs_main(in: VertexOutput) -> @location(0) vec4<f32> {
    let dist = length(in.uv);
    let normThick = global.u_thickness / in.radius;
    let halfThick = normThick * 0.5;
    let ringDist = abs(dist - (1.0 - halfThick));
    let delta = 1.5 / in.radius;
    let alpha = 1.0 - smoothstep(halfThick - delta, halfThick + delta, ringDist);
    return vec4<f32>(in.color.rgb, alpha * in.color.a);
}
`;

const TRAIL_WGSL = `
struct TrailGlobal {
    u_resolution: vec2<f32>,
    u_translate: vec2<f32>,
    u_scale: f32,
    u_borderWidth: f32,
    u_borderColor: vec3<f32>,
    u_padding: f32,
};

struct VertexInput {
    @location(0) position: vec2<f32>,
};

struct InstanceInput {
    @location(1) center: vec2<f32>,
    @location(2) radius: f32,
    @location(3) color: vec3<f32>,
    @location(4) opacity: f32,
};

struct VertexOutput {
    @builtin(position) clipPos: vec4<f32>,
    @location(0) uv: vec2<f32>,
    @location(1) color: vec4<f32>,
    @location(2) radius: f32,
};

@group(0) @binding(0) var<uniform> global: TrailGlobal;

@vertex
fn vs_main(vertex: VertexInput, instance: InstanceInput) -> VertexOutput {
    var out: VertexOutput;
    out.uv = vertex.position;
    out.color = vec4<f32>(instance.color, instance.opacity);
    out.radius = instance.radius;
    let worldPos = (vertex.position * instance.radius) + instance.center;
    let camPos = (worldPos * global.u_scale) + global.u_translate;
    let normalized = (camPos / global.u_resolution) * 2.0 - vec2<f32>(1.0, 1.0);
    out.clipPos = vec4<f32>(normalized.x, -normalized.y, 0.0, 1.0);
    return out;
}

@fragment
fn fs_main(in: VertexOutput) -> @location(0) vec4<f32> {
    let dist = length(in.uv);
    if (dist > 1.0) { discard; }
    
    // Calculate border application
    let border = 1.0 - smoothstep(1.0 - global.u_borderWidth / in.radius, 1.0, dist);
    let alpha = 1.0 - smoothstep(0.8, 1.0, dist);
    
    let baseColor = in.color.rgb;
    let finalColor = mix(baseColor, global.u_borderColor, border);
    
    return vec4<f32>(finalColor, alpha * in.color.a);
}
`;

export class WebGPURenderer implements IRenderer {
  private canvas: HTMLCanvasElement;
  private adapter: any = null;
  private device: any = null;
  private context: any = null;
  private format: string = 'bgra8unorm';

  public isReady = false;
  public onDeviceLost: (() => void) | null = null;

  // GPU Compute
  private computeManager: WebGPUComputeManager | null = null;
  private ballComputeSubsystem: WebGPUBallComputeSubsystem | null = null;
  private gpuParticleSystem: WebGPUParticleSystem | null = null;
  private renderGraph: RenderGraph | null = null;
  private processedCollisionIds = new Set<string>();
  private lastTime = performance.now();

  // Pipeline/Bind Group caches
  private renderPipelines = new Map<string, any>();
  private bindGroupCache: GPUBindGroupCache | null = null;
  private bundleCache: WebGPURenderBundleCache | null = null;

  // Query set for hardware execution timing (WebGPU Timestamp query)
  private querySet: any = null;
  private resolveBuffer: any = null;
  private readbackBuffer: any = null;
  private isQueryPending = false;

  // Buffer resources
  private quadVertexBuffer: any = null;
  private cameraUniformBuffer: any = null;
  private cameraData = new Float32Array(8);

  private instanceBuffer: PersistentBufferRing | null = null;
  private instanceData = new Float32Array(1024 * 8);

  private ringGlobalBuffer: any = null;
  private ringInstanceBuffer: PersistentBufferRing | null = null;
  private ringInstanceData = new Float32Array(1024 * 8);

  private blastGlobalBuffer: any = null;
  private blastInstanceBuffer: PersistentBufferRing | null = null;
  private blastInstanceData = new Float32Array(512 * 8);

  private trailGlobalBuffer: any = null;
  private trailInstanceBuffer: PersistentBufferRing | null = null;
  private trailInstanceData = new Float32Array(4096 * 8);

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.initWebGPU();
  }

  private async initWebGPU() {
    const gpu = (navigator as any).gpu;
    if (!gpu) return;

    try {
      this.adapter = await gpu.requestAdapter();
      if (!this.adapter) return;

      const requiredFeatures = [];
      if (this.adapter.features.has('timestamp-query')) {
        requiredFeatures.push('timestamp-query');
      }

      this.device = await this.adapter.requestDevice({
        requiredFeatures
      });

      this.context = this.canvas.getContext('webgpu');
      if (!this.context) return;

      this.format = gpu.getPreferredCanvasFormat();
      this.context.configure({
        device: this.device,
        format: this.format,
        alphaMode: 'premultiplied'
      });

      this.device.lost.then((info: any) => {
        console.warn(`WebGPU Device Lost: ${info.message}. Forcing fallbacks.`);
        this.isReady = false;
        if (this.onDeviceLost) this.onDeviceLost();
      });

      if (this.device.features.has('timestamp-query')) {
        PerformanceTelemetry.gpuQuerySupported = true;
        this.querySet = this.device.createQuerySet({
          type: 'timestamp',
          count: 2
        });
        this.resolveBuffer = this.device.createBuffer({
          size: 16,
          usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.QUERY_RESOLVE
        });
        this.readbackBuffer = this.device.createBuffer({
          size: 16,
          usage: GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ
        });
      }

      this.bindGroupCache = new GPUBindGroupCache(this.device);
      this.bundleCache = new WebGPURenderBundleCache(this.device);

      this.allocateUnifiedBuffers();
      this.compilePipelines();
      
      try {
        this.computeManager = new WebGPUComputeManager(this.device);
      } catch (computeErr) {
        console.warn('WebGPU Compute shader foundation failed to boot:', computeErr);
      }

      try {
        this.ballComputeSubsystem = new WebGPUBallComputeSubsystem(this.device);
      } catch (ballCompErr) {
        console.warn('WebGPU Ball Compute Subsystem failed to boot:', ballCompErr);
      }

      try {
        this.gpuParticleSystem = new WebGPUParticleSystem(this.device, this.format);
      } catch (particleCompErr) {
        console.warn('WebGPU Particle Subsystem failed to boot:', particleCompErr);
      }

      try {
        this.renderGraph = new RenderGraph(this.device);
      } catch (graphErr) {
        console.warn('WebGPU Render Graph failed to boot:', graphErr);
      }

      this.isReady = true;
    } catch (e) {
      console.error('Error during WebGPU boot sequence:', e);
    }
  }

  private allocateUnifiedBuffers() {
    const vertices = new Float32Array([
      -1, -1, 1, -1, -1, 1,
      -1, 1, 1, -1, 1, 1,
    ]);

    this.quadVertexBuffer = this.device.createBuffer({
      size: vertices.byteLength,
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
      mappedAtCreation: true
    });
    new Float32Array(this.quadVertexBuffer.getMappedRange()).set(vertices);
    this.quadVertexBuffer.unmap();

    this.cameraUniformBuffer = this.device.createBuffer({
      size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    this.ringGlobalBuffer = this.device.createBuffer({
      size: 48, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    this.blastGlobalBuffer = this.device.createBuffer({
      size: 32, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    this.trailGlobalBuffer = this.device.createBuffer({
      size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST
    });

    this.instanceBuffer = new PersistentBufferRing(this.device, 'BallInstanceBuffer', this.instanceData.byteLength, GPUBufferUsage.VERTEX);
    this.ringInstanceBuffer = new PersistentBufferRing(this.device, 'RingInstanceBuffer', this.ringInstanceData.byteLength, GPUBufferUsage.VERTEX);
    this.blastInstanceBuffer = new PersistentBufferRing(this.device, 'BlastInstanceBuffer', this.blastInstanceData.byteLength, GPUBufferUsage.VERTEX);
    this.trailInstanceBuffer = new PersistentBufferRing(this.device, 'TrailInstanceBuffer', this.trailInstanceData.byteLength, GPUBufferUsage.VERTEX);
  }

  private compilePipelines() {
    // 1. BALL PIPELINE
    const ballModule = this.device.createShaderModule({ code: BALL_WGSL });
    const ballBindGroupLayout = this.device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } }]
    });
    const ballPipeline = this.device.createRenderPipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [ballBindGroupLayout] }),
      vertex: {
        module: ballModule, entryPoint: 'vs_main',
        buffers: [
          { arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }] },
          {
            arrayStride: 32, stepMode: 'instance',
            attributes: [
              { shaderLocation: 1, offset: 0, format: 'float32x2' },
              { shaderLocation: 2, offset: 8, format: 'float32x3' },
              { shaderLocation: 3, offset: 20, format: 'float32' },
              { shaderLocation: 4, offset: 24, format: 'float32' }
            ]
          }
        ]
      },
      fragment: {
        module: ballModule, entryPoint: 'fs_main',
        targets: [{
          format: this.format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' }
          }
        }]
      },
      primitive: { topology: 'triangle-list' }
    });
    this.renderPipelines.set('ball', ballPipeline);

    // 2. RING PIPELINE (Escape Containers)
    const ringModule = this.device.createShaderModule({ code: RING_WGSL });
    const ringBindGroupLayout = this.device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }]
    });
    const ringPipeline = this.device.createRenderPipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [ringBindGroupLayout] }),
      vertex: {
        module: ringModule, entryPoint: 'vs_main',
        buffers: [
          { arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }] },
          {
            arrayStride: 32, stepMode: 'instance',
            attributes: [
              { shaderLocation: 1, offset: 0, format: 'float32' },
              { shaderLocation: 2, offset: 4, format: 'float32' },
              { shaderLocation: 3, offset: 8, format: 'float32x3' },
              { shaderLocation: 4, offset: 20, format: 'float32' },
              { shaderLocation: 5, offset: 24, format: 'float32' }
            ]
          }
        ]
      },
      fragment: {
        module: ringModule, entryPoint: 'fs_main',
        targets: [{
          format: this.format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' }
          }
        }]
      },
      primitive: { topology: 'triangle-list' }
    });
    this.renderPipelines.set('ring', ringPipeline);

    // 3. BLAST PIPELINE
    const blastModule = this.device.createShaderModule({ code: BLAST_WGSL });
    const blastBindGroupLayout = this.device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } }]
    });
    const blastPipeline = this.device.createRenderPipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [blastBindGroupLayout] }),
      vertex: {
        module: blastModule, entryPoint: 'vs_main',
        buffers: [
          { arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }] },
          {
            arrayStride: 32, stepMode: 'instance',
            attributes: [
              { shaderLocation: 1, offset: 0, format: 'float32x2' },
              { shaderLocation: 2, offset: 8, format: 'float32' },
              { shaderLocation: 3, offset: 12, format: 'float32x3' },
              { shaderLocation: 4, offset: 24, format: 'float32' }
            ]
          }
        ]
      },
      fragment: {
        module: blastModule, entryPoint: 'fs_main',
        targets: [{
          format: this.format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' }
          }
        }]
      },
      primitive: { topology: 'triangle-list' }
    });
    this.renderPipelines.set('blast', blastPipeline);

    // 4. TRAIL PIPELINE
    const trailModule = this.device.createShaderModule({ code: TRAIL_WGSL });
    const trailBindGroupLayout = this.device.createBindGroupLayout({
      entries: [{ binding: 0, visibility: GPUShaderStage.VERTEX, buffer: { type: 'uniform' } }]
    });
    const trailPipeline = this.device.createRenderPipeline({
      layout: this.device.createPipelineLayout({ bindGroupLayouts: [trailBindGroupLayout] }),
      vertex: {
        module: trailModule, entryPoint: 'vs_main',
        buffers: [
          { arrayStride: 8, attributes: [{ shaderLocation: 0, offset: 0, format: 'float32x2' }] },
          {
            arrayStride: 32, stepMode: 'instance',
            attributes: [
              { shaderLocation: 1, offset: 0, format: 'float32x2' },
              { shaderLocation: 2, offset: 8, format: 'float32' },
              { shaderLocation: 3, offset: 12, format: 'float32x3' },
              { shaderLocation: 4, offset: 24, format: 'float32' }
            ]
          }
        ]
      },
      fragment: {
        module: trailModule, entryPoint: 'fs_main',
        targets: [{
          format: this.format,
          blend: {
            color: { srcFactor: 'src-alpha', dstFactor: 'one-minus-src-alpha', operation: 'add' },
            alpha: { srcFactor: 'one', dstFactor: 'one-minus-src-alpha', operation: 'add' }
          }
        }]
      },
      primitive: { topology: 'triangle-list' }
    });
    this.renderPipelines.set('trail', trailPipeline);
  }

  private getOrCreateBindGroup(cacheKey: string, layout: any, entries: any[]): any {
    if (!this.bindGroupCache) {
      return this.device.createBindGroup({ layout, entries });
    }
    return this.bindGroupCache.getOrCreateBindGroup(cacheKey, layout, entries);
  }

  resize(width: number, height: number) {
    if (!this.isReady) return;
    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: 'premultiplied'
    });
  }

  clear() {
    if (!this.isReady || !this.renderGraph) return;
    this.renderGraph.clear();

    const currentFrameTime = performance.now();
    Promise.resolve().then(() => {
      this.executeRenderGraph(currentFrameTime);
    });
  }

  private executeRenderGraph(now: number) {
    if (!this.isReady || !this.renderGraph) return;

    try {
      const textureView = this.context.getCurrentTexture().createView();
      this.renderGraph.importResource('backbuffer', {
        type: 'texture',
        name: 'backbuffer',
        format: this.format
      }, null, textureView);

      this.renderGraph.execute();
    } catch (err) {
      console.warn('Error executing WebGPU RenderGraph execution pass:', err);
    }
  }

  render(balls: Ball[], config: SimulationConfig, camera: { x: number, y: number, scale: number }, zoomLevel = 1.0) {
    if (!this.isReady || balls.length === 0) return;

    // Apply strict dynamic VRAM budget protection & auto-scaling
    WebGPUVRAMGatekeeper.adjustSettingsToBudget(config);

    // Run GPU background compute sequence
    const now = performance.now();
    const dt = Math.min(0.033, (now - this.lastTime) / 1000);
    this.lastTime = now;

    if (this.computeManager) {
      this.computeManager.update(this.canvas.width, this.canvas.height, dt);
    }

    this.cameraData[0] = this.canvas.width;
    this.cameraData[1] = this.canvas.height;
    this.cameraData[2] = camera.x;
    this.cameraData[3] = camera.y;
    this.cameraData[4] = camera.scale * zoomLevel;
    this.device.queue.writeBuffer(this.cameraUniformBuffer, 0, this.cameraData);

    let finalInstanceBuffer: any = null;
    const stride = 8;
    const requiredLength = balls.length * stride;

    if (this.ballComputeSubsystem) {
      // Step 4.2 & 4.3 - Execute high-speed GPU compute updates & procedural animation effects
      this.ballComputeSubsystem.updateAndSimulate(balls, config, dt, now * 0.001, camera, zoomLevel, this.canvas.width, this.canvas.height);
      finalInstanceBuffer = this.ballComputeSubsystem.getRenderInstancesBuffer() || (this.instanceBuffer ? this.instanceBuffer.getActiveBuffer() : null);
      // Step 4.4 & 4.1 - Read back simulated positions in parallel and copy to sync worker threads
      this.ballComputeSubsystem.readbackDataToBalls(balls);
    } else {
      if (this.instanceData.length < requiredLength) {
        this.instanceData = new Float32Array(Math.max(requiredLength, this.instanceData.length * 2));
      }

      for (let i = 0; i < balls.length; i++) {
        const b = balls[i];
        const offset = i * stride;
        this.instanceData[offset] = b.pos.x;
        this.instanceData[offset + 1] = b.pos.y;

        const colorHex = b.color || '#ffffff';
        this.instanceData[offset + 2] = parseInt(colorHex.slice(1, 3), 16) / 255 || 1.0;
        this.instanceData[offset + 3] = parseInt(colorHex.slice(3, 5), 16) / 255 || 1.0;
        this.instanceData[offset + 4] = parseInt(colorHex.slice(5, 7), 16) / 255 || 1.0;
        this.instanceData[offset + 5] = b.radius;
        this.instanceData[offset + 6] = 0.0; // lodIndex: default to LOD 0
        this.instanceData[offset + 7] = 0.0; // padding to complete stride: 8 floats
      }

      const uploadData = new Float32Array(this.instanceData.buffer, 0, requiredLength);
      finalInstanceBuffer = this.instanceBuffer ? this.instanceBuffer.writeData(uploadData) : null;
    }

    if (this.renderGraph) {
      const isIndirect = !!this.ballComputeSubsystem;
      const indirectBuffer = this.ballComputeSubsystem ? this.ballComputeSubsystem.getIndirectBuffer() : null;
      this.renderGraph.addNode({
        name: 'draw-balls',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: true,
        execute: ({ passEncoder }) => {
          const pipeline = this.renderPipelines.get('ball');
          passEncoder.setPipeline(pipeline);
          
          const bindGroup = this.getOrCreateBindGroup('camera_bg', pipeline.getBindGroupLayout(0), [
            { binding: 0, resource: { buffer: this.cameraUniformBuffer } }
          ]);
          passEncoder.setBindGroup(0, bindGroup);
          passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
          passEncoder.setVertexBuffer(1, finalInstanceBuffer);
          
          if (isIndirect && indirectBuffer) {
            // Task 9.2: Multi-draw indirect for High, Medium, and Low details
            passEncoder.drawIndirect(indirectBuffer, 0);   // LOD 0: High
            passEncoder.drawIndirect(indirectBuffer, 16);  // LOD 1: Medium
            passEncoder.drawIndirect(indirectBuffer, 32);  // LOD 2: Low
          } else {
            passEncoder.draw(6, balls.length);
          }

          // Diagnostics telemetry
          PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
          PerformanceTelemetry.recordVertexUpload(balls.length, requiredLength * 4);
          PerformanceTelemetry.recordIndexCount(6 * balls.length);
        }
      });
    } else {
      const commandEncoder = this.device.createCommandEncoder();

      // Timestamp query start
      const useTimestamp = this.querySet && !this.isQueryPending;
      if (useTimestamp) {
        commandEncoder.writeTimestamp(this.querySet, 0);
      }

      const textureView = this.context.getCurrentTexture().createView();
      const renderPassDescriptor: any = {
        colorAttachments: [{
          view: textureView, clearValue: { r: 0.0, g: 0.0, b: 0.0, a: 0.0 },
          loadOp: 'clear', storeOp: 'store'
        }]
      };

      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      const pipeline = this.renderPipelines.get('ball');
      passEncoder.setPipeline(pipeline);
      
      const bindGroup = this.getOrCreateBindGroup('camera_bg', pipeline.getBindGroupLayout(0), [
        { binding: 0, resource: { buffer: this.cameraUniformBuffer } }
      ]);
      passEncoder.setBindGroup(0, bindGroup);
      passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
      passEncoder.setVertexBuffer(1, finalInstanceBuffer);
      
      if (this.ballComputeSubsystem) {
        const indBuf = this.ballComputeSubsystem.getIndirectBuffer();
        if (indBuf) {
          passEncoder.drawIndirect(indBuf, 0);   // LOD 0
          passEncoder.drawIndirect(indBuf, 16);  // LOD 1
          passEncoder.drawIndirect(indBuf, 32);  // LOD 2
        }
      } else {
        passEncoder.draw(6, balls.length);
      }
      passEncoder.end();

      // Timestamp query end
      if (useTimestamp) {
        commandEncoder.writeTimestamp(this.querySet, 1);
        commandEncoder.resolveQuerySet(this.querySet, 0, 2, this.resolveBuffer, 0);
        commandEncoder.copyBufferToBuffer(this.resolveBuffer, 0, this.readbackBuffer, 0, 16);
        this.isQueryPending = true;
      }
      
      this.device.queue.submit([commandEncoder.finish()]);

      // Handle async readback of queries
      if (useTimestamp) {
        this.readbackBuffer.mapAsync(GPUBufferUsage.MAP_READ).then(() => {
          const arrayBuffer = this.readbackBuffer.getMappedRange();
          const timestamps = new BigUint64Array(arrayBuffer);
          const start = timestamps[0];
          const end = timestamps[1];
          this.readbackBuffer.unmap();
          
          const diff = Number(end - start);
          if (diff > 0 && diff < 1000000000) {
            const durationMs = diff / 1000000;
            PerformanceTelemetry.recordGpuHardwareTime(durationMs);
          }
          this.isQueryPending = false;
        }).catch(() => {
          this.isQueryPending = false;
        });
      }

      // Diagnostics telemetry
      PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
      PerformanceTelemetry.recordVertexUpload(balls.length, requiredLength * 4);
      PerformanceTelemetry.recordIndexCount(6 * balls.length);
    }
  }

  renderBallPreviews(previews: any[], config: SimulationConfig, camera: any, zoomLevel = 1.0) {
    if (!this.isReady || previews.length === 0) return;
    const balls: Ball[] = previews.map((p, i) => ({
      id: `preview-${i}`,
      pos: p.pos,
      vel: p.vel,
      radius: p.radius,
      color: p.color,
      mass: 1,
      angle: Math.atan2(p.vel.y, p.vel.x),
      style: config.ballStyle
    }));
    this.render(balls, { ...config, ballFillOpacity: 0.3, ballStrokeOpacity: 0.3 }, camera, zoomLevel);
  }

  renderContainer(config: SimulationConfig, camera: any, morph: any, radius: number, hue: number, zoomLevel = 1.0) {
    if (!this.isReady) return;

    const ringGlobalData = new Float32Array(12);
    ringGlobalData[0] = this.canvas.width;
    ringGlobalData[1] = this.canvas.height;
    ringGlobalData[2] = camera.x;
    ringGlobalData[3] = camera.y;
    ringGlobalData[4] = camera.scale * zoomLevel;
    ringGlobalData[5] = config.containerLineWidth || 4.0;
    ringGlobalData[6] = 0.0;
    ringGlobalData[7] = 0.0;
    ringGlobalData[8] = this.getShapeId(morph.from);
    ringGlobalData[9] = this.getShapeId(morph.to);
    ringGlobalData[10] = morph.factor;
    ringGlobalData[11] = (config.containerRotation || 0) * Math.PI / 180;
    
    this.device.queue.writeBuffer(this.ringGlobalBuffer, 0, ringGlobalData);

    const iData = new Float32Array(8);
    iData[0] = radius;
    iData[1] = 0;

    const colorHex = config.borderColor || (config.darkMode ? '#ffffff' : '#000000');
    let r = 1.0, g = 1.0, b = 1.0;
    if (config.rgbLighting || config.useContainerRGB) {
      const rgb = this.hslToRgb(hue / 360, 1.0, 0.5);
      r = rgb[0]; g = rgb[1]; b = rgb[2];
    } else {
      r = parseInt(colorHex.slice(1, 3), 16) / 255 || 1.0;
      g = parseInt(colorHex.slice(3, 5), 16) / 255 || 1.0;
      b = parseInt(colorHex.slice(5, 7), 16) / 255 || 1.0;
    }
    iData[2] = r; iData[3] = g; iData[4] = b;
    iData[5] = 1.0;
    iData[6] = 0.0;

    const finalRingInstanceBuffer = this.ringInstanceBuffer ? this.ringInstanceBuffer.writeData(iData) : null;

    if (this.renderGraph) {
      this.renderGraph.addNode({
        name: 'draw-container',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: true,
        execute: ({ passEncoder }) => {
          const pipeline = this.renderPipelines.get('ring');
          const bindGroup = this.getOrCreateBindGroup('ring_bg', pipeline.getBindGroupLayout(0), [
            { binding: 0, resource: { buffer: this.ringGlobalBuffer } }
          ]);

          if (this.bundleCache && finalRingInstanceBuffer) {
            const bundleKey = `container_${getUniqueResourceId(finalRingInstanceBuffer)}`;
            const bundle = this.bundleCache.getOrCreateBundle(bundleKey, this.format, (encoder) => {
              encoder.setPipeline(pipeline);
              encoder.setBindGroup(0, bindGroup);
              encoder.setVertexBuffer(0, this.quadVertexBuffer);
              encoder.setVertexBuffer(1, finalRingInstanceBuffer);
              encoder.draw(6, 1);
            });
            passEncoder.executeBundles([bundle]);
          } else {
            passEncoder.setPipeline(pipeline);
            passEncoder.setBindGroup(0, bindGroup);
            passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
            passEncoder.setVertexBuffer(1, finalRingInstanceBuffer);
            passEncoder.draw(6, 1);
          }

          // Diagnostics telemetry
          PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
          PerformanceTelemetry.recordVertexUpload(1, iData.byteLength);
          PerformanceTelemetry.recordIndexCount(6);
        }
      });
    } else {
      const commandEncoder = this.device.createCommandEncoder();
      const textureView = this.context.getCurrentTexture().createView();
      const renderPassDescriptor: any = {
        colorAttachments: [{ view: textureView, loadOp: 'load', storeOp: 'store' }]
      };

      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      const pipeline = this.renderPipelines.get('ring');
      const bindGroup = this.getOrCreateBindGroup('ring_bg', pipeline.getBindGroupLayout(0), [
        { binding: 0, resource: { buffer: this.ringGlobalBuffer } }
      ]);

      if (this.bundleCache && finalRingInstanceBuffer) {
        const bundleKey = `container_${getUniqueResourceId(finalRingInstanceBuffer)}`;
        const bundle = this.bundleCache.getOrCreateBundle(bundleKey, this.format, (encoder) => {
          encoder.setPipeline(pipeline);
          encoder.setBindGroup(0, bindGroup);
          encoder.setVertexBuffer(0, this.quadVertexBuffer);
          encoder.setVertexBuffer(1, finalRingInstanceBuffer);
          encoder.draw(6, 1);
        });
        passEncoder.executeBundles([bundle]);
      } else {
        passEncoder.setPipeline(pipeline);
        passEncoder.setBindGroup(0, bindGroup);
        passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
        passEncoder.setVertexBuffer(1, finalRingInstanceBuffer);
        passEncoder.draw(6, 1);
      }
      passEncoder.end();

      this.device.queue.submit([commandEncoder.finish()]);

      // Diagnostics telemetry
      PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
      PerformanceTelemetry.recordVertexUpload(1, iData.byteLength);
      PerformanceTelemetry.recordIndexCount(6);
    }
  }

  renderRings(rings: EscapeRing[], config: SimulationConfig, camera: any, morph: any, globalHue: number, zoomLevel = 1.0) {
    if (!this.isReady || rings.length === 0) return;

    const visibleLimit = Math.min(rings.length, config.escapeMaxVisibleRings || 450);
    const count = Math.min(visibleLimit, 1024);

    const ringGlobalData = new Float32Array(12);
    ringGlobalData[0] = this.canvas.width;
    ringGlobalData[1] = this.canvas.height;
    ringGlobalData[2] = camera.x;
    ringGlobalData[3] = camera.y;
    ringGlobalData[4] = camera.scale * zoomLevel;
    ringGlobalData[5] = config.escapeRingThickness || 4.0;
    ringGlobalData[6] = 0.0;
    ringGlobalData[7] = 0.0;
    ringGlobalData[8] = this.getShapeId(morph.from);
    ringGlobalData[9] = this.getShapeId(morph.to);
    ringGlobalData[10] = morph.factor;
    ringGlobalData[11] = (config.containerRotation || 0) * Math.PI / 180;
    
    this.device.queue.writeBuffer(this.ringGlobalBuffer, 0, ringGlobalData);

    const stride = 8;
    const requiredLength = count * stride;
    if (this.ringInstanceData.length < requiredLength) {
      this.ringInstanceData = new Float32Array(Math.max(requiredLength, this.ringInstanceData.length * 2));
    }

    for (let i = 0; i < count; i++) {
      const r = rings[i];
      const offset = i * stride;
      this.ringInstanceData[offset] = r.radius;
      this.ringInstanceData[offset + 1] = r.angle || 0;

      let colR = 1.0, colG = 1.0, colB = 1.0;
      if (config.escapeColorMode === 'RAINBOW') {
        const rgb = this.hslToRgb(((globalHue + i * 8 * (config.escapeRainbowSpeed || 1.0)) % 360) / 360, 1.0, 0.5);
        colR = rgb[0]; colG = rgb[1]; colB = rgb[2];
      } else if (config.escapeColorMode === 'UNIFORM') {
        const hex = config.escapeUniformColor || '#3b82f6';
        colR = parseInt(hex.slice(1, 3), 16) / 255 || 1.0;
        colG = parseInt(hex.slice(3, 5), 16) / 255 || 1.0;
        colB = parseInt(hex.slice(5, 7), 16) / 255 || 1.0;
      } else {
        const hex = config.borderColor || '#1a1a1a';
        colR = parseInt(hex.slice(1, 3), 16) / 255 || 1.0;
        colG = parseInt(hex.slice(3, 5), 16) / 255 || 1.0;
        colB = parseInt(hex.slice(5, 7), 16) / 255 || 1.0;
      }

      this.ringInstanceData[offset + 2] = colR;
      this.ringInstanceData[offset + 3] = colG;
      this.ringInstanceData[offset + 4] = colB;
      this.ringInstanceData[offset + 5] = r.isBreaking ? (1.0 - (r.breakProgress || 0)) : (config.escapeRingAlphaBase || 1.0);

      const currentShift = Math.max(0, config.escapeRingCount - rings.length);
      const originalIndex = currentShift + i;
      const isOpposite = originalIndex < (config.escapeOppositeGapCount || 0);
      const effectiveGapSize = isOpposite ? (config.escapeOppositeGapSize || 30) : (config.escapeGapSize || 30);
      
      let gapRad = (effectiveGapSize * Math.PI / 180);
      if (config.escapeGapAlignment === 'FIXED_SIZE') {
        gapRad = (effectiveGapSize * Math.PI / 180) * config.containerRadius / r.radius;
      }
      this.ringInstanceData[offset + 6] = gapRad;
    }

    const uploadData = new Float32Array(this.ringInstanceData.buffer, 0, requiredLength);
    const finalRingInstanceBuffer = this.ringInstanceBuffer ? this.ringInstanceBuffer.writeData(uploadData) : null;

    if (this.renderGraph) {
      this.renderGraph.addNode({
        name: 'draw-rings',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: true,
        execute: ({ passEncoder }) => {
          const pipeline = this.renderPipelines.get('ring');
          passEncoder.setPipeline(pipeline);

          const bindGroup = this.getOrCreateBindGroup('ring_bg', pipeline.getBindGroupLayout(0), [
            { binding: 0, resource: { buffer: this.ringGlobalBuffer } }
          ]);
          passEncoder.setBindGroup(0, bindGroup);
          passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
          passEncoder.setVertexBuffer(1, finalRingInstanceBuffer);
          passEncoder.draw(6, count);

          // Diagnostics telemetry
          PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
          PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
          PerformanceTelemetry.recordIndexCount(6 * count);
        }
      });
    } else {
      const commandEncoder = this.device.createCommandEncoder();
      const textureView = this.context.getCurrentTexture().createView();
      const renderPassDescriptor: any = {
        colorAttachments: [{ view: textureView, loadOp: 'load', storeOp: 'store' }]
      };

      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      const pipeline = this.renderPipelines.get('ring');
      passEncoder.setPipeline(pipeline);

      const bindGroup = this.getOrCreateBindGroup('ring_bg', pipeline.getBindGroupLayout(0), [
        { binding: 0, resource: { buffer: this.ringGlobalBuffer } }
      ]);
      passEncoder.setBindGroup(0, bindGroup);
      passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
      passEncoder.setVertexBuffer(1, finalRingInstanceBuffer);
      passEncoder.draw(6, count);
      passEncoder.end();

      this.device.queue.submit([commandEncoder.finish()]);

      // Diagnostics telemetry
      PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
      PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
      PerformanceTelemetry.recordIndexCount(6 * count);
    }
  }

  renderBlasts(blasts: any[], config: SimulationConfig, camera: any, zoomLevel = 1.0) {
    if (!this.isReady || blasts.length === 0) return;

    const count = Math.min(blasts.length, 512);

    const blastGlobalData = new Float32Array(8);
    blastGlobalData[0] = this.canvas.width;
    blastGlobalData[1] = this.canvas.height;
    blastGlobalData[2] = camera.x;
    blastGlobalData[3] = camera.y;
    blastGlobalData[4] = camera.scale * zoomLevel;
    blastGlobalData[5] = 5.0; // Standard 5.0 thickness as in WebGL
    this.device.queue.writeBuffer(this.blastGlobalBuffer, 0, blastGlobalData);

    const stride = 8;
    const requiredLength = count * stride;
    if (this.blastInstanceData.length < requiredLength) {
      this.blastInstanceData = new Float32Array(Math.max(requiredLength, this.blastInstanceData.length * 2));
    }

    const now = Date.now();

    for (let i = 0; i < count; i++) {
      const b = blasts[i];
      const elapsed = now - b.startTime;
      const progress = Math.min(1.0, elapsed / (config.blastDuration || 1000));
      const radius = b.maxRadius * progress;
      const alpha = 1.0 - progress;

      const offset = i * stride;
      this.blastInstanceData[offset] = b.pos.x;
      this.blastInstanceData[offset + 1] = b.pos.y;
      this.blastInstanceData[offset + 2] = radius;

      const hex = b.color || '#ffffff';
      this.blastInstanceData[offset + 3] = parseInt(hex.slice(1, 3), 16) / 255 || 1.0;
      this.blastInstanceData[offset + 4] = parseInt(hex.slice(3, 5), 16) / 255 || 1.0;
      this.blastInstanceData[offset + 5] = parseInt(hex.slice(5, 7), 16) / 255 || 1.0;
      this.blastInstanceData[offset + 6] = alpha;
    }

    const uploadData = new Float32Array(this.blastInstanceData.buffer, 0, requiredLength);
    const finalBlastInstanceBuffer = this.blastInstanceBuffer ? this.blastInstanceBuffer.writeData(uploadData) : null;

    if (this.renderGraph) {
      this.renderGraph.addNode({
        name: 'draw-blasts',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: true,
        execute: ({ passEncoder }) => {
          const pipeline = this.renderPipelines.get('blast');
          passEncoder.setPipeline(pipeline);

          const bindGroup = this.getOrCreateBindGroup('blast_bg', pipeline.getBindGroupLayout(0), [
            { binding: 0, resource: { buffer: this.blastGlobalBuffer } }
          ]);
          passEncoder.setBindGroup(0, bindGroup);
          passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
          passEncoder.setVertexBuffer(1, finalBlastInstanceBuffer);
          passEncoder.draw(6, count);

          // Diagnostics telemetry
          PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
          PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
          PerformanceTelemetry.recordIndexCount(6 * count);
        }
      });
    } else {
      const commandEncoder = this.device.createCommandEncoder();
      const textureView = this.context.getCurrentTexture().createView();
      const renderPassDescriptor: any = {
        colorAttachments: [{ view: textureView, loadOp: 'load', storeOp: 'store' }]
      };

      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      const pipeline = this.renderPipelines.get('blast');
      passEncoder.setPipeline(pipeline);

      const bindGroup = this.getOrCreateBindGroup('blast_bg', pipeline.getBindGroupLayout(0), [
        { binding: 0, resource: { buffer: this.blastGlobalBuffer } }
      ]);
      passEncoder.setBindGroup(0, bindGroup);
      passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
      passEncoder.setVertexBuffer(1, finalBlastInstanceBuffer);
      passEncoder.draw(6, count);
      passEncoder.end();

      this.device.queue.submit([commandEncoder.finish()]);

      // Diagnostics telemetry
      PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
      PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
      PerformanceTelemetry.recordIndexCount(6 * count);
    }
  }

  renderTrails(trailCircles: any[], config: SimulationConfig, camera: any, zoomLevel = 1.0) {
    if (!this.isReady || trailCircles.length === 0) return;

    const count = Math.min(trailCircles.length, 4096);

    const trailGlobalData = new Float32Array(16);
    trailGlobalData[0] = this.canvas.width;
    trailGlobalData[1] = this.canvas.height;
    trailGlobalData[2] = camera.x;
    trailGlobalData[3] = camera.y;
    trailGlobalData[4] = camera.scale * zoomLevel;
    
    // Border logic
    const showBorder = config.trailApplyBorderToPersistence || !config.trailPersistence;
    trailGlobalData[5] = showBorder ? (config.trailStrokeWidth || 0) : 0;
    
    const tCol = this.hslToRgb(0, 0, 0); // Need to parse properly
    const hex = config.trailStrokeColor || '#000000';
    trailGlobalData[6] = parseInt(hex.slice(1, 3), 16) / 255;
    trailGlobalData[7] = parseInt(hex.slice(3, 5), 16) / 255;
    trailGlobalData[8] = parseInt(hex.slice(5, 7), 16) / 255;
    
    this.device.queue.writeBuffer(this.trailGlobalBuffer, 0, trailGlobalData);

    const stride = 8;
    const requiredLength = count * stride;
    if (this.trailInstanceData.length < requiredLength) {
      this.trailInstanceData = new Float32Array(Math.max(requiredLength, this.trailInstanceData.length * 2));
    }

    for (let i = 0; i < count; i++) {
      const t = trailCircles[i];
      const offset = i * stride;
      this.trailInstanceData[offset] = t.pos.x;
      this.trailInstanceData[offset + 1] = t.pos.y;
      this.trailInstanceData[offset + 2] = t.radius || 5.0;

      let r = 1.0, g = 1.0, b = 1.0;
      if (config.useTrailRGB !== false) {
        const rgb = this.hslToRgb((t.hue || 0) / 360, 1.0, 0.5);
        r = rgb[0]; g = rgb[1]; b = rgb[2];
      } else {
        const colorHex = config.trailCustomColor || '#ffffff';
        r = (parseInt(colorHex.slice(1, 3), 16) / 255) || 1.0;
        g = (parseInt(colorHex.slice(3, 5), 16) / 255) || 1.0;
        b = (parseInt(colorHex.slice(5, 7), 16) / 255) || 1.0;
      }
      this.trailInstanceData[offset + 3] = r;
      this.trailInstanceData[offset + 4] = g;
      this.trailInstanceData[offset + 5] = b;

      const alphaFade = config.trailPersistence ? 1.0 : (trailCircles.length > 1 ? i / (trailCircles.length - 1) : 1.0);
      this.trailInstanceData[offset + 6] = alphaFade * (config.trailOpacity || 1.0);
    }

    const uploadData = new Float32Array(this.trailInstanceData.buffer, 0, requiredLength);
    const finalTrailInstanceBuffer = this.trailInstanceBuffer ? this.trailInstanceBuffer.writeData(uploadData) : null;

    if (this.renderGraph) {
      this.renderGraph.addNode({
        name: 'draw-trails',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: true,
        execute: ({ passEncoder }) => {
          const pipeline = this.renderPipelines.get('trail');
          passEncoder.setPipeline(pipeline);

          const bindGroup = this.getOrCreateBindGroup('trail_bg', pipeline.getBindGroupLayout(0), [
            { binding: 0, resource: { buffer: this.trailGlobalBuffer } }
          ]);
          passEncoder.setBindGroup(0, bindGroup);
          passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
          passEncoder.setVertexBuffer(1, finalTrailInstanceBuffer);
          passEncoder.draw(6, count);

          // Diagnostics telemetry
          PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
          PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
          PerformanceTelemetry.recordIndexCount(6 * count);
        }
      });
    } else {
      const commandEncoder = this.device.createCommandEncoder();
      const textureView = this.context.getCurrentTexture().createView();
      const renderPassDescriptor: any = {
        colorAttachments: [{ view: textureView, loadOp: 'load', storeOp: 'store' }]
      };

      const passEncoder = commandEncoder.beginRenderPass(renderPassDescriptor);
      const pipeline = this.renderPipelines.get('trail');
      passEncoder.setPipeline(pipeline);

      const bindGroup = this.getOrCreateBindGroup('trail_bg', pipeline.getBindGroupLayout(0), [
        { binding: 0, resource: { buffer: this.trailGlobalBuffer } }
      ]);
      passEncoder.setBindGroup(0, bindGroup);
      passEncoder.setVertexBuffer(0, this.quadVertexBuffer);
      passEncoder.setVertexBuffer(1, finalTrailInstanceBuffer);
      passEncoder.draw(6, count);
      passEncoder.end();

      this.device.queue.submit([commandEncoder.finish()]);

      // Diagnostics telemetry
      PerformanceTelemetry.incrementDrawCalls('webgpu', 1);
      PerformanceTelemetry.recordVertexUpload(count, requiredLength * 4);
      PerformanceTelemetry.recordIndexCount(6 * count);
    }
  }

  renderFX(events: any[], config: SimulationConfig, camera: any, zoomLevel?: number) {}
  
  updateCollisionEvents(collisionEvents: any[], config: SimulationConfig) {
    if (config.gpuParticlesEnabled === false || !this.gpuParticleSystem) return;

    const maxSavedIds = 2000;
    collisionEvents.forEach(e => {
      if (!this.processedCollisionIds.has(e.id)) {
        this.processedCollisionIds.add(e.id);
        
        if (this.processedCollisionIds.size > maxSavedIds) {
          const it = this.processedCollisionIds.values().next();
          if (!it.done) this.processedCollisionIds.delete(it.value);
        }

        const count = Math.max(1, Math.round((config.collisionEffectParticleCount || 20) * FrameScheduler.getLODScale()));
        const speed = config.collisionEffectGravity !== undefined ? 1.0 : 0.8;
        this.gpuParticleSystem!.emit(e.pos.x, e.pos.y, e.color, count, speed, e.duration || 800);
      }
    });
  }

  renderGPUParticles(config: SimulationConfig, camera: any) {
    if (!this.gpuParticleSystem) return;
    const now = performance.now();
    const dt = Math.min(33.3, now - this.lastTime);
    this.lastTime = now;

    if (this.renderGraph) {
      this.renderGraph.addNode({
        name: 'draw-gpu-particles',
        inputs: ['backbuffer'],
        outputs: ['backbuffer'],
        isGroupable: false,
        execute: ({ commandEncoder, resources }) => {
          const textureView = resources.get('backbuffer')?.view;
          if (textureView) {
            this.gpuParticleSystem!.updateAndRenderWithEncoder(config, camera, dt, textureView, commandEncoder);
          }
        }
      });
    } else {
      const textureView = this.context.getCurrentTexture().createView();
      this.gpuParticleSystem.updateAndRender(config, camera, dt, textureView);
    }
  }

  prepareGPUPingPongTrails(config: SimulationConfig) {}
  renderBallsToTrails(balls: Ball[], config: SimulationConfig, camera: any, zoomLevel?: number) {}
  drawGPUTrailsToScreen() {}
  
  resetGPUPasses() {
    if (this.bindGroupCache) {
      this.bindGroupCache.clear();
    }
    if (this.bundleCache) {
      this.bundleCache.clear();
    }
    this.processedCollisionIds.clear();
    if (this.gpuParticleSystem) {
      this.gpuParticleSystem.reset();
    }
    this.lastTime = performance.now();
  }

  private getShapeId(shape: ShapeType): number {
    switch (shape) {
      case 'CIRCLE': return 0;
      case 'TRIANGLE': return 3;
      case 'SQUARE': return 4;
      case 'PENTAGON': return 5;
      case 'HEXAGON': return 6;
      case 'OCTAGON': return 8;
      default: return 0;
    }
  }

  private hslToRgb(h: number, s: number, l: number): [number, number, number] {
    let r, g, b;
    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    return [r, g, b];
  }

  dispose(): void {
    if (this.computeManager) {
      try {
        this.computeManager.dispose();
      } catch (err) {
        console.warn('Error releasing computeManager resources:', err);
      }
      this.computeManager = null;
    }
    if (this.ballComputeSubsystem) {
      try {
        this.ballComputeSubsystem.dispose();
      } catch (err) {
        console.warn('Error releasing ballComputeSubsystem resources:', err);
      }
      this.ballComputeSubsystem = null;
    }
    if (this.gpuParticleSystem) {
      try {
        this.gpuParticleSystem.dispose();
      } catch (err) {
        console.warn('Error releasing gpuParticleSystem resources:', err);
      }
      this.gpuParticleSystem = null;
    }
    if (this.quadVertexBuffer) this.quadVertexBuffer.destroy();
    if (this.cameraUniformBuffer) this.cameraUniformBuffer.destroy();
    if (this.instanceBuffer) this.instanceBuffer.dispose();
    if (this.ringGlobalBuffer) this.ringGlobalBuffer.destroy();
    if (this.ringInstanceBuffer) this.ringInstanceBuffer.dispose();
    if (this.blastGlobalBuffer) this.blastGlobalBuffer.destroy();
    if (this.blastInstanceBuffer) this.blastInstanceBuffer.dispose();
    if (this.trailGlobalBuffer) this.trailGlobalBuffer.destroy();
    if (this.trailInstanceBuffer) this.trailInstanceBuffer.dispose();
    this.renderPipelines.clear();
    if (this.bindGroupCache) {
      this.bindGroupCache.clear();
    }
    if (this.bundleCache) {
      this.bundleCache.clear();
    }
  }
}
